if (!customElements.get('custom-header')) {
  customElements.define(
    'custom-header',
    class CustomHeader extends HTMLElement {
      constructor() {
        super();
        this.lastScrollY = 0;
        this.scrollThreshold = 3;
        this.isScrolling = false;
        this.scrollTimer = null;
        this.ticking = false;
        this.handleScroll = this.handleScroll.bind(this);
        this.updateHeader = this.updateHeader.bind(this);
      }

      connectedCallback() {
        this.updateHeaderHeight = this.updateHeaderHeight.bind(this);
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        window.addEventListener('resize', this.updateHeaderHeight, { passive: true });
        requestAnimationFrame(() => {
          this.updateHeaderHeight();
          this.applyHeaderScrollState(window.scrollY);
        });
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.updateHeaderHeight);
        if (this.scrollTimer) {
          clearTimeout(this.scrollTimer);
        }
      }

      updateHeaderHeight() {
        const height = Math.ceil(this.getBoundingClientRect().height);
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      }

      handleScroll() {
        if (!this.ticking) {
          requestAnimationFrame(this.updateHeader);
          this.ticking = true;
        }

        this.isScrolling = true;

        if (this.scrollTimer) {
          clearTimeout(this.scrollTimer);
        }

        this.scrollTimer = setTimeout(() => {
          this.isScrolling = false;
        }, 150);
      }

      updateHeader() {
        this.ticking = false;
        const currentScrollY = window.scrollY;
        const scrollDiff = Math.abs(currentScrollY - this.lastScrollY);

        if (scrollDiff < this.scrollThreshold && !this.isScrolling) {
          return;
        }

        this.applyHeaderScrollState(currentScrollY);
        this.lastScrollY = currentScrollY;
      }

      /** Header stays visible (no hide-on-scroll). Sticky styling + optional scroll logo after threshold. */
      applyHeaderScrollState(currentScrollY) {
        const stickyThreshold = 200;

        this.style.opacity = '1';
        this.classList.remove('custom-header--hidden', 'custom-header--visible');

        if (currentScrollY > stickyThreshold) {
          this.classList.add('custom-header--sticky');
        } else {
          this.classList.remove('custom-header--sticky');
        }

        if (this.dataset.hasScrollLogo === 'true') {
          this.classList.toggle('custom-header--logo-scrolled', currentScrollY > 80);
        }

        this.updateHeaderHeight();
      }
    }
  );
}

if (!customElements.get('mega-menu')) {
  customElements.define(
    'mega-menu',
    class MegaMenu extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.megaMenuItems = this.querySelectorAll('.mega-menu__item--list');
        this.sharedDrawer = this.querySelector('#shared-mega-menu-drawer');
        this.currentActiveMenuId = null;
        this.currentOverlayHandler = null;

        this.initializeMenuItems();
        this.initializeDrawerEvents();
        this.initializeChildMenuItems();
        this.initializeBottomDropdowns();
        this.initializeSearchHistory();
      }

      initializeMenuItems() {
        this.megaMenuItems.forEach((item) => {
          item.addEventListener('click', (e) => {
            e.preventDefault();
            const menuId = item.getAttribute('data-menu-id');
            if (!menuId) return;

            if (
              this.currentActiveMenuId === menuId &&
              this.sharedDrawer.classList.contains('mega-menu__drawer--open')
            ) {
              this.closeDrawer();
              return;
            }

            if (!this.sharedDrawer.classList.contains('mega-menu__drawer--open')) {
              this.openDrawer(menuId);
              return;
            }

            if (this.currentActiveMenuId !== menuId) {
              this.switchContent(menuId);
            }
          });
        });
      }

      initializeDrawerEvents() {
        if (!this.sharedDrawer) return;

        this.sharedDrawer.addEventListener('click', (e) => {
          if (e.target === this.sharedDrawer) {
            this.closeDrawer();
          }
        });

        const drawerInner = this.sharedDrawer.querySelector('.mega-menu__drawer-inner');
        if (drawerInner) {
          drawerInner.addEventListener('click', (e) => e.stopPropagation());
        }
      }

      initializeChildMenuItems() {
        const childMenuItems = this.querySelectorAll('.mega-menu__link-link--child');
        const drawerInner = this.querySelector('.mega-menu__drawer-inner');
        const drawerRight = this.querySelector('.mega-menu__drawer-right');

        if (!drawerInner || !drawerRight) return;

        childMenuItems.forEach((childItem) => {
          childItem.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            const itemId = childItem.id;
            const targetContent = drawerRight.querySelector(`[data-content="${itemId}"]`);
            const allMenuLinks = this.querySelectorAll('.mega-menu__link-link');

            if (!targetContent) return;

            const isCurrentlyVisible = targetContent.style.display === 'flex';

            if (isCurrentlyVisible) {
              this.hideChildContent(childItem, targetContent, drawerRight, drawerInner, allMenuLinks);
            } else {
              this.showChildContent(childItem, targetContent, drawerRight, drawerInner, allMenuLinks, childMenuItems);
            }
          });
        });
      }

      hideChildContent(childItem, targetContent, drawerRight, drawerInner, allMenuLinks) {
        targetContent.style.display = 'none';
        drawerRight.style.display = 'none';
        drawerInner.style.maxWidth = '490px';
        childItem.classList.remove('mega-menu__link-link--active');

        allMenuLinks.forEach((link) => {
          link.classList.remove('mega-menu__link-link--dimmed');
        });
      }

      showChildContent(childItem, targetContent, drawerRight, drawerInner, allMenuLinks, childMenuItems) {
        childMenuItems.forEach((item) => {
          item.classList.remove('mega-menu__link-link--active');
        });

        allMenuLinks.forEach((link) => {
          link.classList.remove('mega-menu__link-link--dimmed');
        });

        const allContents = drawerRight.querySelectorAll('[data-content]');
        allContents.forEach((content) => {
          content.style.display = 'none';
        });

        setTimeout(() => {
          targetContent.style.display = 'flex';
        }, 200);

        childItem.classList.add('mega-menu__link-link--active');

        allMenuLinks.forEach((link) => {
          if (link !== childItem) {
            link.classList.add('mega-menu__link-link--dimmed');
          }
        });

        drawerRight.style.display = 'block';
        drawerInner.style.maxWidth = '1070px';
        drawerInner.style.transition = 'max-width 0.3s ease';
      }

      initializeBottomDropdowns() {
        const bottomDropdownLinks = this.querySelectorAll('.mega-menu__bottom-link--dropdown');

        bottomDropdownLinks.forEach((dropdownLink) => {
          dropdownLink.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            const siblingUl = dropdownLink.nextElementSibling;
            if (siblingUl && siblingUl.tagName === 'UL') {
              siblingUl.classList.toggle('mega-menu__bottom-list--open');
              dropdownLink.classList.toggle('mega-menu__bottom-link--active');
            }
          });
        });
      }

      openDrawer(menuId) {
        const drawerOverlay = this.querySelector('#drawer-overlay');

        this.currentActiveMenuId = menuId;
        this.resetDrawerContent();
        this.showContent(menuId);

        document.body.style.overflow = 'hidden';

        if (drawerOverlay) {
          drawerOverlay.classList.add('mega-menu__overlay--visible');

          this.currentOverlayHandler = (e) => {
            e.stopPropagation();
            this.closeDrawer();
          };

          drawerOverlay.addEventListener('click', this.currentOverlayHandler);
        }

        this.sharedDrawer.classList.add('mega-menu__drawer--open');

        if (menuId === 'search') {
          const searchInput = this.sharedDrawer.querySelector('.search__input');
          if (searchInput) {
            setTimeout(() => {
              searchInput.focus();
              this.showSearchHistory('desktop');
            }, 100);
          }
        }
      }

      switchContent(newMenuId) {
        const currentContentWrapper = this.sharedDrawer.querySelector(`[data-content="${this.currentActiveMenuId}"]`);
        const newContentWrapper = this.sharedDrawer.querySelector(`[data-content="${newMenuId}"]`);

        if (!currentContentWrapper || !newContentWrapper) return;

        currentContentWrapper.style.opacity = '0';
        currentContentWrapper.style.transition = 'opacity 0.2s ease';

        setTimeout(() => {
          this.resetAllContentStates();
          this.hideAllContent();
          this.showContent(newMenuId);
          this.currentActiveMenuId = newMenuId;

          newContentWrapper.style.opacity = '0';
          newContentWrapper.style.transition = 'opacity 0.2s ease';

          setTimeout(() => {
            newContentWrapper.style.opacity = '1';
          }, 50);

          currentContentWrapper.style.opacity = '';
          currentContentWrapper.style.transition = '';
        }, 200);
      }

      showContent(menuId) {
        const contentWrapper = this.sharedDrawer.querySelector(`[data-content="${menuId}"]`);
        if (contentWrapper) {
          contentWrapper.style.display = 'flex';
        }
        this.updateMenuItemStates(menuId);
      }

      updateMenuItemStates(activeMenuId) {
        const allMenuLinks = this.querySelectorAll('.mega-menu__link');
        allMenuLinks.forEach((link) => {
          link.classList.remove('mega-menu__link--active', 'mega-menu__link--inactive');
        });

        const allMenuItems = this.querySelectorAll('.mega-menu__item--list');
        allMenuItems.forEach((item) => {
          const menuId = item.getAttribute('data-menu-id');
          const menuLink = item.querySelector('.mega-menu__link');

          if (menuId === activeMenuId) {
            menuLink?.classList.add('mega-menu__link--active');
          } else {
            menuLink?.classList.add('mega-menu__link--inactive');
          }
        });
      }

      hideAllContent() {
        const allContentWrappers = this.sharedDrawer.querySelectorAll('.mega-menu__content-wrapper');
        allContentWrappers.forEach((wrapper) => {
          wrapper.style.display = 'none';
        });
      }

      resetAllContentStates() {
        if (!this.sharedDrawer) return;

        const drawerInner = this.sharedDrawer.querySelector('.mega-menu__drawer-inner');
        if (drawerInner) {
          drawerInner.style.maxWidth = '490px';
          drawerInner.style.transition = '';
        }

        const allContentWrappers = this.sharedDrawer.querySelectorAll('.mega-menu__content-wrapper');
        allContentWrappers.forEach((wrapper) => {
          const childMenuItems = wrapper.querySelectorAll('.mega-menu__link-link--child');
          const allMenuLinks = wrapper.querySelectorAll('.mega-menu__link-link');
          const drawerRight = wrapper.querySelector('.mega-menu__drawer-right');
          const bottomDropdownLists = wrapper.querySelectorAll('.mega-menu__bottom-list--open');

          childMenuItems.forEach((item) => {
            item.classList.remove('mega-menu__link-link--active');
          });

          allMenuLinks.forEach((link) => {
            link.classList.remove('mega-menu__link-link--dimmed');
          });

          if (drawerRight) {
            drawerRight.style.display = 'none';
            const allContents = drawerRight.querySelectorAll('[data-content]');
            allContents.forEach((content) => {
              content.style.display = 'none';
            });
          }

          bottomDropdownLists.forEach((list) => {
            list.classList.remove('mega-menu__bottom-list--open');
          });

          const searchInputs = wrapper.querySelectorAll('.search__input');
          searchInputs.forEach((input) => {
            input.value = '';
            input.blur();

            const predictiveSearch = input.closest('predictive-search');
            if (predictiveSearch) {
              predictiveSearch.close(true);
            }
          });
        });
      }

      closeDrawer() {
        const drawerOverlay = this.querySelector('#drawer-overlay');

        document.body.style.overflow = '';
        this.sharedDrawer.classList.remove('mega-menu__drawer--open');

        if (drawerOverlay) {
          drawerOverlay.classList.remove('mega-menu__overlay--visible');

          if (this.currentOverlayHandler) {
            drawerOverlay.removeEventListener('click', this.currentOverlayHandler);
            this.currentOverlayHandler = null;
          }
        }

        this.resetMenuItemStates();
        this.currentActiveMenuId = null;
        this.hideAllContent();
        this.resetDrawerContent();
      }

      resetMenuItemStates() {
        const allMenuLinks = this.querySelectorAll('.mega-menu__link');
        allMenuLinks.forEach((link) => {
          link.classList.remove('mega-menu__link--active', 'mega-menu__link--inactive');
        });
      }

      resetDrawerContent() {
        if (!this.sharedDrawer) return;

        const drawerInner = this.sharedDrawer.querySelector('.mega-menu__drawer-inner');
        const childMenuItems = this.sharedDrawer.querySelectorAll('.mega-menu__link-link--child');
        const allMenuLinks = this.sharedDrawer.querySelectorAll('.mega-menu__link-link');
        const bottomDropdownLists = this.sharedDrawer.querySelectorAll('.mega-menu__bottom-list--open');
        const searchInputs = this.sharedDrawer.querySelectorAll('.search__input');
        const drawerRights = this.sharedDrawer.querySelectorAll('.mega-menu__drawer-right');

        if (drawerInner) {
          drawerInner.style.opacity = '';
          drawerInner.style.transition = '';
          drawerInner.style.maxWidth = '490px';
        }

        drawerRights.forEach((drawerRight) => {
          const allContents = drawerRight.querySelectorAll('[data-content]');
          allContents.forEach((content) => {
            content.style.display = 'none';
          });
          drawerRight.style.display = 'none';
        });

        childMenuItems.forEach((item) => {
          item.classList.remove('mega-menu__link-link--active');
        });

        allMenuLinks.forEach((link) => {
          link.classList.remove('mega-menu__link-link--dimmed');
        });

        bottomDropdownLists.forEach((list) => {
          list.classList.remove('mega-menu__bottom-list--open');
        });

        searchInputs.forEach((input) => {
          input.value = '';
          input.blur();

          const predictiveSearch = input.closest('predictive-search');
          if (predictiveSearch) {
            predictiveSearch.close(true);
          }
        });
      }

      initializeSearchHistory() {
        const searchInputs = this.querySelectorAll('.mega-menu__search-input');
        searchInputs.forEach((input) => {
          input.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
              const searchTerm = input.value.trim();
              if (searchTerm) {
                this.saveSearchHistory(searchTerm);
              }
            }
          });

          input.addEventListener('focus', () => {
            this.showSearchHistory('desktop');
          });
        });

        this.querySelectorAll('#clear-history-desktop').forEach((button) => {
          button.addEventListener('click', () => this.clearSearchHistory());
        });
      }

      saveSearchHistory(searchTerm) {
        if (!searchTerm?.trim()) return;

        try {
          let history = JSON.parse(localStorage.getItem('searchHistory') || '[]');
          if (!Array.isArray(history)) history = [];

          history = [searchTerm, ...history.filter((term) => term !== searchTerm)].slice(0, 10);
          localStorage.setItem('searchHistory', JSON.stringify(history));

          this.showSearchHistory('desktop');
          document.querySelector('mobile-menu')?.showSearchHistory('mobile');
        } catch (error) {
          console.error('Error saving search history:', error);
        }
      }

      showSearchHistory(type) {
        try {
          const history = JSON.parse(localStorage.getItem('searchHistory') || '[]');
          const container = this.querySelector(`#search-history-${type}`);
          const list = this.querySelector(`#search-history-list-${type}`);

          if (!container || !list) return;

          if (!Array.isArray(history) || !history.length) {
            container.style.display = 'none';
            return;
          }

          container.style.display = 'block';
          list.innerHTML = history
            .filter((term) => term?.trim())
            .map((term) => {
              const escapedTerm = term.replace(/'/g, '&#39;').replace(/"/g, '&quot;');
              return `<li><a href="/search?q=${encodeURIComponent(
                term
              )}" onclick="event.preventDefault();const input=document.querySelector('.${type === 'desktop' ? 'mega-menu' : 'mobile-search'
                }__search-input');if(input){input.value='${escapedTerm}';input.closest('form')?.submit();}">${escapedTerm}</a></li>`;
            })
            .join('');
        } catch (error) {
          console.error('Error showing search history:', error);
          localStorage.removeItem('searchHistory');
        }
      }

      clearSearchHistory() {
        localStorage.removeItem('searchHistory');
        ['desktop', 'mobile'].forEach((type) => {
          const container = document.querySelector(`#search-history-${type}`);
          if (container) container.style.display = 'none';
        });
      }
    }
  );
}

if (!customElements.get('mobile-menu')) {
  customElements.define(
    'mobile-menu',
    class MobileMenu extends HTMLElement {
      constructor() {
        super();
        this.body = document.body;
        this.drawer = this.querySelector('#mobile-menu__drawer');
        this.drawerContent = this.querySelector('.mobile-menu__drawer-body-top');
        this.formPanel = this.querySelector('#inquiry-form-panel--mobile');
        this.subMenuContent = this.querySelector('.mobile-menu__drawer-submenu-body');
        this.currentActiveMenuId = null;
        this.isDrawerOpen = false;
        this.initializeEventListeners();
      }

      initializeEventListeners() {
        this.initializeDrawerToggle();
        this.initializeTabFunctionality();
        this.initializeSubMenus();
        this.initializeBottomDropdowns();
        this.initializeMobileContactDrawer();
        this.initializeMobileSearchDrawer();
      }

      initializeDrawerToggle() {
        this.querySelector('#mobile-menu__drawer-btn--open')?.addEventListener('click', () => this.toggleDrawer());

        this.querySelectorAll('.mobile-menu__drawer-btn--close').forEach((button) => {
          button.addEventListener('click', () => this.toggleDrawer(true));
        });

        this.drawer?.addEventListener('click', (e) => {
          if (e.target === this.drawer) this.toggleDrawer(true);
        });
      }

      initializeTabFunctionality() {
        this.querySelectorAll('.mobile-menu__item--list').forEach((item) => {
          item.addEventListener('click', (e) => {
            e.preventDefault();
            const menuId = item.getAttribute('data-mobile-menu-id');
            if (menuId) this.switchMobileContent(menuId);
          });
        });
      }

      initializeSubMenus() {
        this.querySelectorAll('.mobile-menu__drawer-list-link--child').forEach((trigger) => {
          trigger.addEventListener('click', (e) => {
            e.preventDefault();
            const subMenuDropdown = trigger.parentElement.querySelector('.mobile-menu__drawer-submenu');
            if (subMenuDropdown) {
              setTimeout(() => this.drawerContent?.scrollTo({ top: 0, behavior: 'smooth' }), 400);
              subMenuDropdown.classList.add('mobile-menu__drawer-submenu--active');
            }
          });
        });

        this.querySelectorAll('.mobile-menu__drawer-btn--back').forEach((button) => {
          button.addEventListener('click', () => this.goBackFromSubmenu(button));
        });
      }

      initializeBottomDropdowns() {
        this.querySelectorAll('.mobile-menu__bottom-link--dropdown').forEach((link) => {
          link.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const ul = link.nextElementSibling;
            if (ul?.tagName === 'UL') {
              ul.classList.toggle('mobile-menu__bottom-list--open');
              link.classList.toggle('mobile-menu__bottom-link--active');
            }
          });
        });
      }

      toggleDrawer(forceClose = false) {
        this.isDrawerOpen || forceClose ? this.closeDrawer() : this.openDrawer();
      }

      openDrawer() {
        if (!this.drawer) return;

        const mobileContactDrawer = this.querySelector('#mobile-contact-drawer');
        if (mobileContactDrawer) {
          mobileContactDrawer.classList.remove('mobile-contact-drawer--active');
        }

        this.resetAllDropdowns();

        setTimeout(() => {
          if (this.drawerContent) {
            this.drawerContent.scrollTop = 0;
          }
        }, 0);

        this.drawer.style.display = 'block';

        setTimeout(() => {
          const content = this.drawer.querySelector('.mobile-menu__drawer-content');
          if (content) {
            content.style.transform = 'translateX(0)';
          }
        }, 10);

        this.body.style.overflow = 'hidden';
        this.isDrawerOpen = true;
        this.showDefaultContent();
      }

      closeDrawer() {
        if (!this.drawer) return;

        const content = this.drawer.querySelector('.mobile-menu__drawer-content');
        if (content) {
          content.style.transform = 'translateX(-100%)';
        }

        if (this.formPanel && this.formPanel.classList.contains('active')) {
          this.formPanel.classList.remove('active');
          setTimeout(() => {
            this.formPanel.classList.add('hidden');
          }, 300);
        }

        setTimeout(() => {
          this.drawer.style.display = 'none';
          this.resetDrawer();

          this.body.style.overflow = '';
          this.isDrawerOpen = false;
        }, 300);

        if (document.querySelector('#mobile-contact-back').classList.contains('hidden')) {
          document.querySelector('#mobile-contact-back').classList.remove('hidden');
        }
      }

      goBackFromSubmenu(button) {
        const subMenuDropdown = button?.closest('.mobile-menu__drawer-submenu');
        subMenuDropdown?.classList.remove('mobile-menu__drawer-submenu--active');

        this.subMenuContent?.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }

      resetDrawer() {
        this.querySelectorAll('.mobile-menu__drawer-submenu--active').forEach((menu) =>
          menu.classList.remove('mobile-menu__drawer-submenu--active')
        );

        const mobileContactDrawer = this.querySelector('#mobile-contact-drawer');
        if (mobileContactDrawer) {
          mobileContactDrawer.classList.remove('mobile-contact-drawer--active');
        }

        this.resetAllDropdowns();

        setTimeout(() => {
          if (this.drawerContent) {
            this.drawerContent.scrollTop = 0;
          }
        }, 0);
      }

      resetAllDropdowns() {
        const dropdownLinks = this.querySelectorAll('.mobile-menu__bottom-link--dropdown');
        dropdownLinks.forEach((link) => {
          link.classList.remove('mobile-menu__bottom-link--active');
        });

        const dropdownLists = this.querySelectorAll('.mobile-menu__bottom-list--open');
        dropdownLists.forEach((list) => {
          list.classList.remove('mobile-menu__bottom-list--open');
        });
      }

      switchMobileContent(newMenuId) {
        this.hideAllMobileContent();
        this.showMobileContent(newMenuId);
        this.updateMobileMenuItemStates(newMenuId);
        this.currentActiveMenuId = newMenuId;

        setTimeout(() => {
          if (this.drawerContent) {
            this.drawerContent.scrollTop = 0;
          }
        }, 0);
      }

      showMobileContent(menuId) {
        const contentWrapper = this.drawerContent?.querySelector(`[data-mobile-content="${menuId}"]`);
        if (contentWrapper) {
          contentWrapper.style.display = 'flex';
        }
      }

      hideAllMobileContent() {
        const allContentWrappers = this.drawerContent?.querySelectorAll('[data-mobile-content]') || [];
        allContentWrappers.forEach((wrapper) => {
          wrapper.style.display = 'none';
        });
      }

      updateMobileMenuItemStates(activeMenuId) {
        const allMenuLinks = this.querySelectorAll('.mobile-menu__link');
        allMenuLinks.forEach((link) => {
          link.classList.remove('mobile-menu__link--active');
        });

        const allMenuItems = this.querySelectorAll('.mobile-menu__item--list');
        allMenuItems.forEach((item) => {
          const menuId = item.getAttribute('data-mobile-menu-id');
          const menuLink = item.querySelector('.mobile-menu__link');

          if (menuId === activeMenuId && menuLink) {
            menuLink.classList.add('mobile-menu__link--active');
          }
        });
      }

      showDefaultContent() {
        const firstMenuItem = this.querySelector('.mobile-menu__item--list');
        if (firstMenuItem) {
          const firstMenuId = firstMenuItem.getAttribute('data-mobile-menu-id');
          if (firstMenuId) {
            this.switchMobileContent(firstMenuId);
          }
        }
      }

      initializeMobileContactDrawer() {
        const mobileContactTrigger = this.querySelector('#mobile-contact-trigger');
        const mobileContactDrawer = this.querySelector('#mobile-contact-drawer');
        const mobileContactBack = this.querySelector('#mobile-contact-back');
        const mobileContactClose = this.querySelector('#mobile-contact-close');
        const drawerContent = this.querySelector('.mobile-menu__drawer-body-top');

        if (!mobileContactTrigger || !mobileContactDrawer) return;

        // Open mobile contact drawer
        mobileContactTrigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.openMobileContactDrawer(mobileContactDrawer, drawerContent);
        });

        // Back button - go back to main mobile menu
        if (mobileContactBack) {
          mobileContactBack.addEventListener('click', () => {
            this.closeMobileContactDrawer(mobileContactDrawer, drawerContent);
          });
        }

        // Close button - close entire mobile menu
        if (mobileContactClose) {
          mobileContactClose.addEventListener('click', () => {
            this.closeMobileContactDrawer(mobileContactDrawer, drawerContent);
            this.toggleDrawer(true); // Close the entire mobile menu
          });
        }
      }

      openMobileContactDrawer(drawer, drawerContent) {
        if (!drawer || !drawerContent) return;

        // Scroll to top of main drawer content
        setTimeout(() => {
          drawerContent.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }, 50);

        // Show mobile contact drawer
        drawer.classList.add('mobile-contact-drawer--active');
      }

      closeMobileContactDrawer(drawer, drawerContent) {
        if (!drawer) return;

        drawer.classList.remove('mobile-contact-drawer--active');

        // Scroll back to top when returning to main menu
        if (drawerContent) {
          setTimeout(() => {
            drawerContent.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
          }, 300);
        }
      }

      initializeMobileSearchDrawer() {
        const mobileSearchButton = this.querySelector('#mobile-search__drawer-btn--open');
        const mobileSearchDrawer = document.querySelector('#mobile-search__drawer');
        const mobileSearchClose = document.querySelector('.mobile-search__drawer-btn--close');

        if (!mobileSearchButton || !mobileSearchDrawer) return;

        mobileSearchButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.openMobileSearchDrawer(mobileSearchDrawer);
        });

        if (mobileSearchClose) {
          mobileSearchClose.addEventListener('click', () => {
            this.closeMobileSearchDrawer(mobileSearchDrawer);
          });
        }

        mobileSearchDrawer.addEventListener('click', (e) => {
          if (e.target === mobileSearchDrawer) {
            this.closeMobileSearchDrawer(mobileSearchDrawer);
          }
        });

        this.initializeMobileSearchFunctionality();
      }

      initializeMobileSearchFunctionality() {
        const searchInputs = document.querySelectorAll('.mobile-search__search-input');
        searchInputs.forEach((input) => {
          input.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
              const searchTerm = input.value.trim();
              if (searchTerm) {
                this.saveSearchHistory(searchTerm);
              }
            }
          });

          input.addEventListener('focus', () => {
            this.showSearchHistory('mobile');
          });
        });

        const clearHistoryButtons = document.querySelectorAll('#clear-history-mobile');
        clearHistoryButtons.forEach((button) => {
          button.addEventListener('click', () => this.clearSearchHistory());
        });
      }

      openMobileSearchDrawer(drawer) {
        if (!drawer) return;

        drawer.style.display = 'block';

        setTimeout(() => {
          const content = drawer.querySelector('.mobile-search__drawer-content');
          if (content) {
            content.style.transform = 'translateX(0)';
          }
        }, 10);

        this.body.style.overflow = 'hidden';

        setTimeout(() => {
          this.showSearchHistory('mobile');
          const searchInput = drawer.querySelector('.mobile-search__search-input');
          if (searchInput) {
            searchInput.focus();
          }
        }, 100);
      }

      closeMobileSearchDrawer(drawer) {
        if (!drawer) return;

        const content = drawer.querySelector('.mobile-search__drawer-content');
        if (content) {
          content.style.transform = 'translateX(-100%)';
        }

        setTimeout(() => {
          drawer.style.display = 'none';
        }, 300);

        this.body.style.overflow = '';
      }

      saveSearchHistory(searchTerm) {
        if (!searchTerm || searchTerm.trim() === '') return;

        try {
          let searchHistory = JSON.parse(localStorage.getItem('searchHistory') || '[]');

          // Ensure searchHistory is an array
          if (!Array.isArray(searchHistory)) {
            searchHistory = [];
          }

          // Remove duplicate if exists
          searchHistory = searchHistory.filter((term) => term !== searchTerm);

          // Add to beginning of array
          searchHistory.unshift(searchTerm);

          // Keep only last 10 searches
          searchHistory = searchHistory.slice(0, 10);

          localStorage.setItem('searchHistory', JSON.stringify(searchHistory));

          this.showSearchHistory('mobile');

          const megaMenu = document.querySelector('mega-menu');
          if (megaMenu) {
            megaMenu.showSearchHistory('desktop');
          }
        } catch (error) {
          console.error('Error saving search history:', error);
        }
      }

      showSearchHistory(type) {
        try {
          const searchHistory = JSON.parse(localStorage.getItem('searchHistory') || '[]');
          const historyContainer = document.querySelector(`#search-history-${type}`);
          const historyList = document.querySelector(`#search-history-list-${type}`);

          if (!historyContainer || !historyList) return;

          // Ensure searchHistory is an array
          if (!Array.isArray(searchHistory) || searchHistory.length === 0) {
            historyContainer.style.display = 'none';
            return;
          }

          historyContainer.style.display = 'block';
          historyList.innerHTML = searchHistory
            .filter((term) => term?.trim())
            .map((term) => {
              const escapedTerm = term.replace(/'/g, '&#39;').replace(/"/g, '&quot;');
              return `<li><a href="/search?q=${encodeURIComponent(
                term
              )}" onclick="event.preventDefault();const input=document.querySelector('.${type === 'desktop' ? 'mega-menu' : 'mobile-search'
                }__search-input');if(input){input.value='${escapedTerm}';input.closest('form')?.submit();}">${escapedTerm}</a></li>`;
            })
            .join('');
        } catch (error) {
          console.error('Error showing search history:', error);
          localStorage.removeItem('searchHistory');
        }
      }

      clearSearchHistory() {
        localStorage.removeItem('searchHistory');
        ['desktop', 'mobile'].forEach((type) => {
          const container = document.querySelector(`#search-history-${type}`);
          if (container) container.style.display = 'none';
        });
      }
    }
  );
}

document.addEventListener('DOMContentLoaded', () => {
  const elements = {
    drawer: document.querySelector('#contact-drawer'),
    overlay: document.querySelector('#contact-drawer-overlay'),
    triggers: document.querySelectorAll('.contact-drawer-trigger'),
    closeBtn: document.querySelector('#contact-drawer-close'),
    content: document.querySelector('.contact-drawer__content'),
    formPanel: document.querySelector('#inquiry-form-panel'),
  };

  let isOpen = false;
  let overlayHandler = null;

  const toggleDrawer = (open = !isOpen) => {
    if (!elements.drawer || !elements.overlay) return;

    isOpen = open;
    document.body.style.overflow = open ? 'hidden' : '';

    if (open) {
      if (overlayHandler) elements.overlay.removeEventListener('click', overlayHandler);
      overlayHandler = (e) => e.target === elements.overlay && (e.stopPropagation(), toggleDrawer(false));
      elements.overlay.addEventListener('click', overlayHandler);
      elements.overlay.classList.add('contact-drawer__overlay--visible');
      setTimeout(() => elements.drawer.classList.add('contact-drawer--open'));
    } else {
      elements.drawer.classList.remove('contact-drawer--open');
      elements.overlay.classList.remove('contact-drawer__overlay--visible');
      if (elements.formPanel && elements.formPanel.classList.contains('active')) {
        elements.formPanel.classList.remove('active');
        setTimeout(() => {
          elements.formPanel.classList.add('hidden');
        }, 300);
      }
      if (overlayHandler) {
        elements.overlay.removeEventListener('click', overlayHandler);
        overlayHandler = null;
      }
    }
  };

  window.toggleContactDrawer = toggleDrawer;

  elements.closeBtn?.addEventListener('click', () => toggleDrawer(false));
  elements.content?.addEventListener('click', (e) => e.stopPropagation());
  document.addEventListener('keydown', (e) => e.key === 'Escape' && isOpen && toggleDrawer(false));

  // Use event delegation to handle contact drawer triggers (works for accordion content too)
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('.contact-drawer-trigger');
    if (!trigger) return;

    e.preventDefault();
    e.stopPropagation();

    if (window.innerWidth >= 1200) {
      toggleDrawer(true);
    } else {
      // For mobile/tablet, open the mobile contact drawer
      const mobileMenuDrawer = document.querySelector('#mobile-menu__drawer');
      const mobileContactDrawer = document.querySelector('#mobile-contact-drawer');
      const drawerContent = document.querySelector('.mobile-menu__drawer-body-top');
      const mobileDrawerContent = document.querySelector('.mobile-menu__drawer-content');

      if (mobileContactDrawer) {
        // First open the mobile menu drawer if not already open
        if (mobileMenuDrawer) {
          mobileMenuDrawer.style.display = 'block';
          document.body.style.overflow = 'hidden';

          setTimeout(() => {
            if (mobileDrawerContent) {
              mobileDrawerContent.style.transform = 'translateX(0)';
            }

            // Then show the contact drawer after a small delay
            setTimeout(() => {
              if (drawerContent) {
                drawerContent.scrollTo({ top: 0, behavior: 'smooth' });
              }
              mobileContactDrawer.classList.add('mobile-contact-drawer--active');
            }, 100);
          }, 10);
        }
      }
    }
  });
});
